import React from 'react';
import { FieldInfo, ValueMapping } from '../../models';
import ValueMappingEditor from './ValueMappingEditor';
import TransformConfigSection from './TransformConfigSection';
import FieldChips from '../shared/FieldChips';
import PreviewValueDisplay, { formatPreviewValue } from '../shared/PreviewValueDisplay';
import { useFieldFormState } from '../../hooks/useFieldFormState';
import { Dialog, Button, Input, Card } from '../ui';
import { ArrowLeft } from '../ui/icons';

type FieldConfigSelector = {
  selector: string;
  attribute?: string;
  transform?: any;
  defaultValue?: any;
  valueMappings?: ValueMapping[];
};

interface FieldConfigSharedProps {
  field: FieldInfo;
  selector: FieldConfigSelector;
  onUpdate: (selector: FieldConfigSelector) => void;
  onRemove?: () => void;
  previewValue?: unknown;
  previewLoading?: boolean;
  onRefreshPreview?: () => void;
  onPreviewTargetChange?: (fieldId: string) => void;
  onSelectorFocus?: (selector: string) => void;
}

interface FieldConfigFormProps extends FieldConfigSharedProps {
  isActive?: boolean;
}

export interface FieldConfigDialogProps extends FieldConfigSharedProps {
  open: boolean;
  onClose: () => void;
}

export interface FieldConfigInlinePanelProps extends FieldConfigSharedProps {
  onClose: () => void;
  title?: string;
}

const FieldConfigForm: React.FC<FieldConfigFormProps> = React.memo(
  ({
    field,
    selector,
    onUpdate,
    previewValue,
    previewLoading,
    onRefreshPreview,
    onPreviewTargetChange,
    onSelectorFocus,
    isActive = true,
  }) => {
    const isEmpty =
      !selector.selector || selector.selector === '__PLACEHOLDER__';
    const isMultiple = field.multiple === true;
    const isComplex = field.complex === true;

    const {
      localValues,
      updateLocalValue,
      handleSelectorBlur,
      handleAttributeBlur,
      handleDefaultValueBlur,
      handleValueMappingsChange,
      handleTransformTypeChange,
      handleTransformParamBlur,
      handleSubTransformTypeChange,
      handleSubTransformParamBlur,
      handleSubParamBlur,
    } = useFieldFormState({
      selector,
      onUpdate,
      isMultiple,
      isActive,
    });

    // Set preview target when form becomes active
    React.useEffect(() => {
      if (isActive) {
        onPreviewTargetChange?.(field.id);
      }
    }, [field.id, onPreviewTargetChange, isActive]);

    return (
      <div className="space-y-6">
        <div>
          <FieldChips field={field} />
          <p className="text-sm text-gray-600 mt-2">
            {field.description}
          </p>
        </div>

        <div className="space-y-4">
          <Input
            label="CSS Selector"
            value={localValues.selector}
            onChange={(e) => updateLocalValue('selector', (e.target as HTMLInputElement).value)}
            onBlur={handleSelectorBlur}
            onFocus={() => onSelectorFocus?.(localValues.selector)}
            fullWidth
            placeholder={
              isComplex
                ? `e.g., .${field.id}-container`
                : isMultiple
                ? `e.g., .${field.id}-item`
                : `e.g., .${field.id}`
            }
          />

          <div>
            <Input
              label="Attribute (optional)"
              value={localValues.attribute}
              onChange={(e) => updateLocalValue('attribute', (e.target as HTMLInputElement).value)}
              onBlur={handleAttributeBlur}
              fullWidth
              placeholder={
                isComplex
                  ? 'e.g., data-object'
                  : isMultiple
                  ? 'e.g., data-items'
                  : 'e.g., href, src, data-value'
              }
              disabled={isEmpty}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isComplex
                ? 'For complex objects'
                : isMultiple
                ? 'For multiple items'
                : 'Leave empty for text content'}
            </p>
          </div>

          <TransformConfigSection
            fieldId={field.id}
            transform={selector.transform}
            localValues={localValues}
            isEmpty={isEmpty}
            onLocalValueChange={updateLocalValue}
            onTransformTypeChange={handleTransformTypeChange}
            onTransformParamBlur={handleTransformParamBlur}
            onSubTransformTypeChange={handleSubTransformTypeChange}
            onSubTransformParamBlur={handleSubTransformParamBlur}
            onSubParamBlur={handleSubParamBlur}
          />

          <div>
            <Input
              label={isMultiple ? 'Default value (comma separated)' : 'Default value'}
              value={localValues.defaultValue}
              onChange={(e) => updateLocalValue('defaultValue', (e.target as HTMLInputElement).value)}
              onBlur={handleDefaultValueBlur}
              fullWidth
              disabled={isEmpty}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isMultiple
                ? 'Comma-separated list of fallback values'
                : 'Optional fallback value when selector has no match'}
            </p>
          </div>
        </div>

        {field.type === 'valueList' && !isEmpty && (
          <ValueMappingEditor
            field={field}
            valueMappings={selector.valueMappings || []}
            onValueMappingsChange={handleValueMappingsChange}
          />
        )}

        {isComplex && field.objectProperties && !isEmpty && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Object Properties
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              For complex objects, you'll need to use a custom transform
              function to extract these properties:
            </p>
            {field.objectProperties.map((prop) => (
              <div key={prop.key} className="mb-2">
                <p className="text-sm text-gray-900">
                  <strong>{prop.label}</strong> (property: {prop.key})
                </p>
              </div>
            ))}
          </div>
        )}

        <PreviewValueDisplay
          previewValue={previewValue}
          isMultiple={isMultiple}
          previewLoading={previewLoading}
          onRefreshPreview={onRefreshPreview}
        />
      </div>
    );
  }
);

const FieldConfigDialog: React.FC<FieldConfigDialogProps> = React.memo(
  ({ open, onClose, onRemove, field, ...formProps }) => (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Configure ${field.name}`}
      maxWidth="2xl"
      footer={
        <div className="flex justify-between items-center w-full">
          {onRemove ? (
            <Button variant="danger" onClick={onRemove}>
              Remove field
            </Button>
          ) : (
            <div />
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <FieldConfigForm
        field={field}
        {...formProps}
        isActive={open}
      />
    </Dialog>
  )
);

export const FieldConfigInlinePanel: React.FC<FieldConfigInlinePanelProps> = ({
  onClose,
  onRemove,
  title,
  field,
  ...formProps
}) => (
  <Card className="border border-gray-300 rounded-lg p-4 flex flex-col h-full">
    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to fields
      </Button>
      <h2 className="text-lg font-semibold flex-1 text-center">
        {title ?? `Configure ${field.name}`}
      </h2>
    </div>
    <div className="flex-1 overflow-y-auto pr-2">
      <FieldConfigForm field={field} {...formProps} isActive />
    </div>
    <div className="mt-4 flex justify-between flex-wrap gap-2">
      {onRemove ? (
        <Button variant="danger" onClick={onRemove}>
          Remove field
        </Button>
      ) : (
        <div />
      )}
      <Button variant="primary" onClick={onClose}>
        Done
      </Button>
    </div>
  </Card>
);

FieldConfigDialog.displayName = 'FieldConfigDialog';

export default FieldConfigDialog;

export { formatPreviewValue };
